import * as ApolloReactHooks from '@apollo/react-hooks';
import { useEffect } from 'react';
import { useHistory } from 'react-router';

import { AuthUser } from '../../../generated/graphql';
import { session } from '../../../utils/auth';

type IdentityConfirmationHook<M, V extends { bank: string }> = (
  options?: ApolloReactHooks.MutationHookOptions<M, V>
) => ApolloReactHooks.MutationTuple<M, V>;

// we also receive any for `__typename` field
type AuthUserMutation = { [k: string]: AuthUser | any };

type UseIdentityConfirmationOptions<M, V> = ApolloReactHooks.MutationHookOptions<M, V>;

/**
 * hook for identity confirmation mutation, generic over mutation type and variables
 * @param useConfirmationHook graphql mutation hook. It must receive bank variable as BankType type
 * @param key key of mutation result
 * @param authURLOrCallback url to redirect to or callback to be called
 *                          after successful confirmation
 * @param opts options for mutation hook
 * @returns confirmation mutation result
 */
export const useIdentityConfirmation = <M extends AuthUserMutation, V extends { bank: string }>(
  useConfirmationHook: IdentityConfirmationHook<M, V>,
  key: keyof M,
  authURLOrCallback: string | (() =>void),
  opts?: UseIdentityConfirmationOptions<M, V>,
) => {
  const history = useHistory();
  const [ confirmation, result ] = useConfirmationHook(opts);
  const { loading, called, error, data } = result;
  const finished = !loading && called && !error;

  useEffect(() => {
    const asyncEffect = async () => {
      if (finished && data) {
        const { access, refresh } = data[key];
        session.setAccessToken(access);
        session.setRefreshToken(refresh);

        if (typeof authURLOrCallback === 'function') authURLOrCallback();
        else history.push(authURLOrCallback);
      }
    };

    asyncEffect();
  }, [
    history,
    finished,
    data,
    key,
    authURLOrCallback,
  ]);

  return [ confirmation, { ...result, finished } ] as const;
};
